import { OFFICIAL_CANTONS, CANTON_COLOR_MAP } from "./constants";

/**
 * Canton Normalization Utilities
 *
 * Handles the complex business logic for Swiss canton data transformation
 * with two distinct modes:
 *
 * PREVIEW MODE: Shows 6 primary cantons + "Other" group
 * EXPANDED MODE: Shows 6 primary + 13 secondary cantons + "Other" group
 *
 * This dual-mode system allows progressive disclosure of detail while
 * maintaining clean visualizations in both compact and detailed views.
 */

// Centralized canton configuration
export const CANTON_CONFIG = {
  codes: OFFICIAL_CANTONS,
  // Primary cantons shown in preview mode (highest investment activity)
  primaryPreviewCodes: ["ZH", "VD", "ZG", "GE", "BS", "BE"] as const,
  // Additional cantons shown when expanded
  expandedCodes: [
    "SG",
    "TI",
    "LU",
    "AG",
    "SZ",
    "VS",
    "FR",
    "BL",
    "NE",
    "SO",
    "SH",
    "TG",
    "JU",
  ] as const,
  // Small cantons always grouped as "Other"
  otherCodes: ["GR", "AR", "OW", "NW", "UR", "GL", "AI"] as const,
} as const;

// Mapping from API canton codes (e.g., "ZH", "VD") to full Swiss names
const CANTON_CODE_TO_NAME: Record<string, string> = {
  ZH: "Zürich",
  BE: "Bern",
  LU: "Luzern",
  UR: "Uri",
  SZ: "Schwyz",
  OW: "Obwalden",
  NW: "Nidwalden",
  GL: "Glarus",
  ZG: "Zug",
  FR: "Fribourg",
  SO: "Solothurn",
  BS: "Basel-Stadt",
  BL: "Basel-Landschaft",
  SH: "Schaffhausen",
  AR: "Appenzell Ausserrhoden",
  AI: "Appenzell Innerrhoden",
  SG: "St. Gallen",
  GR: "Graubünden",
  AG: "Aargau",
  TG: "Thurgau",
  TI: "Ticino",
  VD: "Vaud",
  VS: "Valais",
  NE: "Neuchâtel",
  GE: "Genève",
  JU: "Jura",
  Abroad: "Other",
};

/**
 * Normalize canton from code or name to full official name
 *
 * Handles both:
 * - API codes (AG, VD, ZH) → Full names (Aargau, Vaud, Zürich)
 * - Full names (pass-through validation)
 *
 * Returns null for invalid/unknown values
 */
export const normalizeCanton = (rawCanton?: string | null): string | null => {
  if (!rawCanton) return null;

  // Check if it's a recognized canton code from API
  if (CANTON_CODE_TO_NAME[rawCanton]) {
    return CANTON_CODE_TO_NAME[rawCanton];
  }

  // Verify it's already a valid full canton name
  const isOfficialName = OFFICIAL_CANTONS.some((c) => c.name === rawCanton);
  if (isOfficialName) return rawCanton;

  return null;
};

/**
 * Normalize canton for preview mode
 *
 * Groups non-primary cantons as "Other" for compact visualization.
 * Preview mode shows only the 6 most active cantons individually.
 */
export const normalizeForPreview = (rawCanton: string | null | undefined): string | null => {
  const normalized = normalizeCanton(rawCanton);
  if (!normalized) return null;

  // Get list of primary canton names for preview
  const primaryNames = CANTON_CONFIG.primaryPreviewCodes
    .map((code) => OFFICIAL_CANTONS.find((c) => c.code === code)?.name)
    .filter(Boolean) as string[];

  // Group all non-primary cantons as "Other"
  if (!primaryNames.includes(normalized)) {
    return "Other";
  }
  return normalized;
};

/**
 * Normalize canton for expanded mode
 *
 * Shows primary + expanded cantons (19 total), groups remainder as "Other".
 * This provides more detail while still avoiding visual clutter from
 * low-activity cantons.
 */
export const normalizeForExpanded = (rawCanton: string | null | undefined): string | null => {
  const normalized = normalizeCanton(rawCanton);
  if (!normalized) return null;

  const primaryNames = CANTON_CONFIG.primaryPreviewCodes
    .map((code) => OFFICIAL_CANTONS.find((c) => c.code === code)?.name)
    .filter(Boolean) as string[];

  const expandedNames = CANTON_CONFIG.expandedCodes
    .map((code) => OFFICIAL_CANTONS.find((c) => c.code === code)?.name)
    .filter(Boolean) as string[];

  // Show individually if in primary or expanded list
  if (primaryNames.includes(normalized) || expandedNames.includes(normalized)) {
    return normalized;
  }
  return "Other";
};

/**
 * Get canton color from predefined color map
 */
export const getCantonColor = (canton: string): string => {
  return CANTON_COLOR_MAP[canton] || "#6B7280";
};

/**
 * Get primary canton names for preview mode
 */
export const getPrimaryCantonNames = (): string[] => {
  return CANTON_CONFIG.primaryPreviewCodes
    .map((code) => OFFICIAL_CANTONS.find((c) => c.code === code)?.name)
    .filter(Boolean) as string[];
};

/**
 * Get all expanded canton names (primary + expanded list)
 */
export const getExpandedCantonNames = (): string[] => {
  const primaryNames = getPrimaryCantonNames();
  const expandedNames = CANTON_CONFIG.expandedCodes
    .map((code) => OFFICIAL_CANTONS.find((c) => c.code === code)?.name)
    .filter(Boolean) as string[];

  return [...primaryNames, ...expandedNames];
};
